import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Car } from 'lucide-react';

const LoadingScreen = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const duration = 3000; // 3 secondes
    const steps = 100;
    const stepDuration = duration / steps;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(() => {
            if (onLoadingComplete) onLoadingComplete();
          }, 500);
          return 100;
        }
        return prev + 1;
      });
    }, stepDuration);

    return () => clearInterval(timer);
  }, [onLoadingComplete]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800"
    >
      {/* Particules en arrière-plan */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-blue-400/30 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        ))}
      </div>

      {/* Contenu principal */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo FACTORY */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="mb-8"
        >
          <div className="text-6xl font-bold text-white text-center">
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              FACTORY By
            </span>
            <br />
            <span className="text-3xl text-gray-300"> CFA Est Loiret</span>
          </div>
        </motion.div>

        {/* Conteneur de la voiture */}
        <div className="relative w-80 h-80 mb-8">
          {/* SVG de la voiture */}
          <svg
            viewBox="0 0 200 120"
            className="w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Définition du dégradé pour le remplissage */}
            <defs>
              <linearGradient id="fillGradient" x1="0%" y1="100%" x2="0%" y2="0%">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8" />
                <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#93c5fd" stopOpacity="0.4" />
              </linearGradient>
              
              {/* Filtre pour l'effet de brillance */}
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>

            {/* Contour de la voiture */}
            <g className="car-outline" stroke="#60a5fa" strokeWidth="2" fill="none">
              {/* Corps principal */}
              <path d="M 30 70 L 40 70 L 45 60 L 65 60 L 70 50 L 130 50 L 135 60 L 155 60 L 160 70 L 170 70 L 170 90 L 30 90 Z" />
              
              {/* Toit */}
              <path d="M 70 50 L 75 35 L 125 35 L 130 50" />
              
              {/* Fenêtres */}
              <path d="M 77 38 L 82 50 L 100 50 L 100 38 Z" fill="#1e293b" />
              <path d="M 105 38 L 105 50 L 123 50 L 118 38 Z" fill="#1e293b" />
              
              {/* Roues */}
              <circle cx="55" cy="90" r="12" stroke="#60a5fa" strokeWidth="3" fill="#1e293b" />
              <circle cx="145" cy="90" r="12" stroke="#60a5fa" strokeWidth="3" fill="#1e293b" />
              <circle cx="55" cy="90" r="6" fill="#334155" />
              <circle cx="145" cy="90" r="6" fill="#334155" />
              
              {/* Détails */}
              <line x1="45" y1="70" x2="48" y2="70" strokeWidth="1" />
              <line x1="152" y1="70" x2="155" y2="70" strokeWidth="1" />
            </g>

            {/* Remplissage animé */}
            <g clipPath="url(#carClip)">
              <defs>
                <clipPath id="carClip">
                  <path d="M 30 70 L 40 70 L 45 60 L 65 60 L 70 50 L 130 50 L 135 60 L 155 60 L 160 70 L 170 70 L 170 90 L 30 90 Z" />
                  <path d="M 70 50 L 75 35 L 125 35 L 130 50" />
                </clipPath>
              </defs>
              
              <motion.rect
                x="30"
                y="35"
                width="140"
                height="55"
                fill="url(#fillGradient)"
                filter="url(#glow)"
                initial={{ y: 90 }}
                animate={{ y: 90 - (progress * 0.55) }}
                transition={{ duration: 0.1 }}
              />
              
              {/* Effet de vagues */}
              <motion.path
                d={`M 30 ${90 - (progress * 0.55)} Q 60 ${88 - (progress * 0.55)} 100 ${90 - (progress * 0.55)} T 170 ${90 - (progress * 0.55)} L 170 90 L 30 90 Z`}
                fill="#3b82f6"
                opacity="0.3"
                animate={{
                  d: [
                    `M 30 ${90 - (progress * 0.55)} Q 60 ${88 - (progress * 0.55)} 100 ${90 - (progress * 0.55)} T 170 ${90 - (progress * 0.55)} L 170 90 L 30 90 Z`,
                    `M 30 ${90 - (progress * 0.55)} Q 60 ${92 - (progress * 0.55)} 100 ${90 - (progress * 0.55)} T 170 ${90 - (progress * 0.55)} L 170 90 L 30 90 Z`,
                    `M 30 ${90 - (progress * 0.55)} Q 60 ${88 - (progress * 0.55)} 100 ${90 - (progress * 0.55)} T 170 ${90 - (progress * 0.55)} L 170 90 L 30 90 Z`,
                  ]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </g>

            {/* Reflets */}
            <motion.circle
              cx="90"
              cy={progress > 50 ? "45" : "100"}
              r="3"
              fill="white"
              opacity={progress > 50 ? "0.6" : "0"}
              animate={{
                opacity: [0.6, 0.3, 0.6],
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: 1,
                repeat: Infinity
              }}
            />
          </svg>
        </div>

        {/* Pourcentage */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5 }}
          className="relative"
        >
          <div className="text-7xl font-bold text-white mb-4">
            <motion.span
              key={progress}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"
            >
              {progress}%
            </motion.span>
          </div>
        </motion.div>

        {/* Barre de progression */}
        <div className="w-80 h-2 bg-slate-700/50 rounded-full overflow-hidden backdrop-blur-sm">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>

        {/* Texte de chargement */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 text-center"
        >
          <motion.p
            animate={{
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="text-gray-300 text-lg"
          >
            Chargement de votre page...
          </motion.p>
          <div className="flex justify-center space-x-2 mt-4">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 bg-blue-400 rounded-full"
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.3, 1, 0.3],
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LoadingScreen;